'use client';

import React from 'react';
import { Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getReactionMetadata } from '@/lib/config/reactions';
import clsx from 'clsx';

interface ReactionDialogProps {
  availableReactions: string[];
  userReactions: string[];
  engagementSummary?: any;
  onReactionClick: (reactionType: string) => void;
  isSignedIn?: boolean;
  isApproved?: boolean;
  isPending: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  details?: React.ReactNode;
}

export function ReactionDialog({
  availableReactions,
  userReactions,
  engagementSummary,
  onReactionClick,
  isSignedIn,
  isApproved,
  open,
  onOpenChange,
  title,
  details,
}: ReactionDialogProps) {
  const getReactionCount = (reactionType: string): number => {
    return engagementSummary?.engagement?.reactions?.by_type?.[reactionType]?.count || 0;
  };

  // Combine available reactions with user's existing reactions (for legacy support)
  // This ensures users can see and remove reactions they already have, even if those
  // reaction types are no longer available for new reactions
  const allReactionsToShow = React.useMemo(() => {
    const reactionSet = new Set([...availableReactions, ...userReactions]);
    return Array.from(reactionSet);
  }, [availableReactions, userReactions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title || 'Reactions'}</DialogTitle>
          {isSignedIn && <div>Click to set your reactions</div>}
          {!isSignedIn && (
            <p className="text-xs text-center text-gray-500 mt-2">Sign in to add reactions</p>
          )}
        </DialogHeader>
        {details && <div className="text-sm text-gray-600 -mt-2 mb-2">{details}</div>}
        <div className="space-y-2 mt-4 overflow-y-auto flex-1">
          {allReactionsToShow.map((reactionType) => {
            const metadata = getReactionMetadata(reactionType);
            if (!metadata) return null;

            const count = getReactionCount(reactionType);
            const isActive = userReactions.includes(reactionType);
            const isAvailable = availableReactions.includes(reactionType);
            const isLegacy = isActive && !isAvailable;

            const canInteract = isSignedIn && !(isLegacy && !isActive);

            return (
              <button
                key={reactionType}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  // Don't do anything if user can't interact
                  if (!canInteract) return;
                  onReactionClick(reactionType);
                }}
                className={clsx(
                  'w-full flex gap-3 items-center p-3 rounded-lg transition-all',
                  'border-2',
                  canInteract && 'hover:bg-gray-50 cursor-pointer',
                  isActive && 'bg-primary-100 border-primary-500 shadow-sm',
                  !isActive && 'border-gray-200',
                  isLegacy && 'opacity-75'
                )}
              >
                <span className="text-2xl flex-shrink-0">{metadata.emoji}</span>
                <div className="flex-1 text-left">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {metadata.label}
                    {isLegacy && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded uppercase font-semibold">
                        Legacy
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {count > 0 && (
                    <span className="text-sm font-semibold text-gray-600 min-w-[2rem] text-right">
                      {count}
                    </span>
                  )}
                  {isActive && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
